// src/Dashboard/pages/teacher/GradeCN/components/GradeCNDataTable.jsx

import React from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

import {
  formatDateFr,
  getAdherentName,
  getAdherentPhoto,
  getBeltLabel,
  getBeltPhotoById,
  getNextBeltNameById,
  getNextBeltPhotoById,
  getUvCompletion,
  DEFAULT_BELT_PHOTO,
  DEFAULT_USER_PHOTO,
} from "../utils/gradeCNDashboardUtils";

function UvBadge({ label, active }) {
  return (
    <span
      className={`inline-flex min-w-[52px] items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-bold ${
        active
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700"
      }`}
    >
      {label} {active ? "✓" : "—"}
    </span>
  );
}

function ProgressBar({ completion }) {
  return (
    <div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]"
          style={{ width: `${completion.percent}%` }}
        />
      </div>

      <p className="mt-1 text-[11px] font-bold text-slate-500 dark:text-slate-400">
        {completion.done}/{completion.total} UV · {completion.percent}%
      </p>
    </div>
  );
}

export default function GradeCNDataTable({
  grades = [],
  belts = [],
  beltHistories = {},
  deletingId,
  onEdit,
  onDelete,
}) {
  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/60">
        <table className="min-w-[1280px] w-full">
          <thead>
            <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <th className="px-4 py-3 text-left font-bold">Adhérent</th>
              <th className="px-4 py-3 text-left font-bold">Dernier passage</th>
              <th className="px-4 py-3 text-left font-bold">
                Ceinture actuelle
              </th>
              <th className="px-4 py-3 text-left font-bold">Objectif</th>
              <th className="px-4 py-3 text-left font-bold">Dominante</th>
              <th className="px-4 py-3 text-left font-bold">UV</th>
              <th className="px-4 py-3 text-left font-bold">Avancement</th>
              <th className="px-4 py-3 text-left font-bold">Points UV3</th>
              <th className="px-4 py-3 text-left font-bold">Observations</th>
              <th className="sticky right-0 z-20 w-32 bg-slate-50 px-4 py-3 text-right font-bold dark:bg-slate-900">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {grades.map((grade) => {
              const adherent = grade?.Adherent || {};
              const history = beltHistories?.[grade.adherent_id];
              const completion = getUvCompletion(grade);

              const currentBeltPhoto = history?.belt_id
                ? getBeltPhotoById(belts, history.belt_id)
                : DEFAULT_BELT_PHOTO;

              const currentBelt = history?.belt_id
                ? belts.find((belt) => Number(belt.id) === Number(history.belt_id))
                : null;

              const currentBeltName = currentBelt ? getBeltLabel(currentBelt) : "—";

              const nextBeltPhoto = history?.belt_id
                ? getNextBeltPhotoById(belts, history.belt_id)
                : DEFAULT_BELT_PHOTO;

              const nextBeltName = history?.belt_id
                ? getNextBeltNameById(belts, history.belt_id)
                : "—";

              return (
                <tr
                  key={grade.id}
                  className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-4 py-3">
                    <div className="flex min-w-[220px] items-center gap-3">
                      <img
                        src={getAdherentPhoto(adherent)}
                        alt={getAdherentName(adherent)}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_USER_PHOTO;
                        }}
                        className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      />

                      <div className="min-w-0">
                        <p className="truncate font-bold text-slate-900 dark:text-white">
                          {getAdherentName(adherent) || "—"}
                        </p>

                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          Saison {adherent?.saison_active || "—"}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-200">
                    {history ? formatDateFr(history.date_passage) : "—"}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex min-w-[150px] flex-col gap-1">
                      <img
                        src={currentBeltPhoto}
                        alt={currentBeltName}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_BELT_PHOTO;
                        }}
                        className="h-8 w-fit object-contain"
                      />

                      <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                        {currentBeltName}
                      </span>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex min-w-[150px] flex-col gap-1">
                      <img
                        src={nextBeltPhoto}
                        alt={nextBeltName}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_BELT_PHOTO;
                        }}
                        className="h-8 w-fit object-contain"
                      />

                      <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                        {nextBeltName}
                      </span>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                        grade.dominante === "Technique"
                          ? "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                          : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                      }`}
                    >
                      {grade.dominante || "—"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex min-w-[250px] flex-wrap gap-1.5">
                      <UvBadge label="UV1" active={completion.uv1} />
                      <UvBadge label="UV2" active={completion.uv2} />
                      <UvBadge label="UV3" active={completion.uv3} />
                      <UvBadge label="UV4" active={completion.uv4} />
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="min-w-[160px]">
                      <ProgressBar completion={completion} />
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex min-w-[52px] justify-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {Number(grade.uv3_points || 0)}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <p className="line-clamp-2 max-w-[280px] text-slate-600 dark:text-slate-300">
                      {grade.observations || "—"}
                    </p>
                  </td>

                  <td className="sticky right-0 z-10 bg-white px-4 py-3 dark:bg-slate-900">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(grade)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        title="Modifier"
                      >
                        <FaEdit />
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete(grade)}
                        disabled={deletingId === grade.id}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                        title="Supprimer"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}